import { EClientNotificationsEventTypes } from './client-notifications.enum';

/** Presentation metadata for a notification, derived from its `Origin`. */
export interface IClientNotificationCategory {
  /** Translation key for the category name (`NOTIFICATIONS.CATEGORY.*`). */
  label: string;
  /** Flaticon icon class (rounded-regular pack, e.g. `fi-rr-bell`). */
  icon: string;
  /**
   * Tailwind class for the avatar background (e.g. `bg-purple-fade`). Kept as a
   * full literal string — Tailwind JIT does not detect classes built via
   * interpolation, so dynamically composed pairs would not work.
   */
  bgClass: string;
  /** Tailwind class for the icon color (e.g. `text-purple-foreground`). */
  iconColorClass: string;
}

/**
 * Maps each `Origin` (`EClientNotificationsEventTypes`) to the category name
 * and icon shown in the notification item. For origins without an explicit
 * entry, use {@link DEFAULT_CLIENT_NOTIFICATION_CATEGORY}.
 */
export const CLIENT_NOTIFICATION_CATEGORY_MAP: Record<
  EClientNotificationsEventTypes,
  IClientNotificationCategory
> = {
  [EClientNotificationsEventTypes.onCentralNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.GENERAL',
    icon: 'fi-rr-bell',
    bgClass: 'bg-purple-fade',
    iconColorClass: 'text-purple-foreground',
  },
  [EClientNotificationsEventTypes.onCentralNotificationContract]: {
    label: 'NOTIFICATIONS.CATEGORY.CONTRACT',
    icon: 'fi-rr-file-signature',
    bgClass: 'bg-blue-fade',
    iconColorClass: 'text-blue-foreground',
  },
  [EClientNotificationsEventTypes.onGympassCheckinNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.WELLHUB',
    icon: 'fi-rr-user-check',
    bgClass: 'bg-pink-fade',
    iconColorClass: 'text-pink-foreground',
  },
  [EClientNotificationsEventTypes.onTotalpassCheckinNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.TOTALPASS',
    icon: 'fi-rr-user-check',
    bgClass: 'bg-emerald-fade',
    iconColorClass: 'text-emerald-foreground',
  },
  [EClientNotificationsEventTypes.onInvoicesZipCreatedNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.INVOICES',
    icon: 'fi-rr-file-invoice',
    bgClass: 'bg-orange-fade',
    iconColorClass: 'text-orange-foreground',
  },
  [EClientNotificationsEventTypes.onAccessRealTimeOccupancyNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.OCCUPANCY',
    icon: 'fi-rr-users-alt',
    bgClass: 'bg-cyan-fade',
    iconColorClass: 'text-cyan-foreground',
  },
  [EClientNotificationsEventTypes.onCompletedConsumptionNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CONSUMPTION',
    icon: 'fi-rr-shopping-cart-check',
    bgClass: 'bg-lime-fade',
    iconColorClass: 'text-lime-foreground',
  },
  [EClientNotificationsEventTypes.onUserPhotoUploadedNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.PHOTO',
    icon: 'fi-rr-camera',
    bgClass: 'bg-purple-fade',
    iconColorClass: 'text-purple-foreground',
  },
  [EClientNotificationsEventTypes.onHotsitePersonCreatedNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.REGISTRATION',
    icon: 'fi-rr-user-add',
    bgClass: 'bg-blue-fade',
    iconColorClass: 'text-blue-foreground',
  },
  [EClientNotificationsEventTypes.onSubscriptionReceivementNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.FINANCIAL',
    icon: 'fi-rr-sack-dollar',
    bgClass: 'bg-success-fade',
    iconColorClass: 'text-success-foreground',
  },
  [EClientNotificationsEventTypes.onReceivementErrorNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.FINANCIAL',
    icon: 'fi-rr-triangle-warning',
    bgClass: 'bg-warning-fade',
    iconColorClass: 'text-warning-foreground',
  },
  [EClientNotificationsEventTypes.onCrmEventNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CRM',
    icon: 'fi-rr-megaphone',
    bgClass: 'bg-orange-fade',
    iconColorClass: 'text-orange-foreground',
  },
  [EClientNotificationsEventTypes.onCrmEventProgressNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CRM',
    icon: 'fi-rr-time-forward',
    bgClass: 'bg-orange-fade',
    iconColorClass: 'text-orange-foreground',
  },
  [EClientNotificationsEventTypes.onContractNotSentEventProgressNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CONTRACT',
    icon: 'fi-rr-envelope',
    bgClass: 'bg-blue-fade',
    iconColorClass: 'text-blue-foreground',
  },
  [EClientNotificationsEventTypes.onContractPendingEventProgressNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CONTRACT',
    icon: 'fi-rr-envelope',
    bgClass: 'bg-blue-fade',
    iconColorClass: 'text-blue-foreground',
  },
  [EClientNotificationsEventTypes.onContractEventNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.CONTRACT',
    icon: 'fi-rr-file-signature',
    bgClass: 'bg-blue-fade',
    iconColorClass: 'text-blue-foreground',
  },
  [EClientNotificationsEventTypes.onInactivationDailyNotification]: {
    label: 'NOTIFICATIONS.CATEGORY.INACTIVATION',
    icon: 'fi-rr-user-xmark',
    bgClass: 'bg-danger-fade',
    iconColorClass: 'text-danger-foreground',
  },
};

/** Fallback for origins not mapped in {@link CLIENT_NOTIFICATION_CATEGORY_MAP}. */
export const DEFAULT_CLIENT_NOTIFICATION_CATEGORY: IClientNotificationCategory = {
  label: 'NOTIFICATIONS.CATEGORY.GENERAL',
  icon: 'fi-rr-bell',
  bgClass: 'bg-purple-fade',
  iconColorClass: 'text-purple-foreground',
};

/** Resolves the category metadata for an `Origin`, with a safe fallback. */
export function getClientNotificationCategory(
  origin?: string
): IClientNotificationCategory {
  if (!origin) {
    return DEFAULT_CLIENT_NOTIFICATION_CATEGORY;
  }
  return (
    CLIENT_NOTIFICATION_CATEGORY_MAP[origin as EClientNotificationsEventTypes] ??
    DEFAULT_CLIENT_NOTIFICATION_CATEGORY
  );
}
